import { createSearchParams, generatePath as originalGeneratePath } from 'react-router-dom';

import { PathParams, PathnameEnum } from './router.types';

type GetPathParams<Pathname> = Pathname extends keyof PathParams
  ? [PathParams[Pathname]]
  : [never?];

export const generatePath = <Pathname extends PathnameEnum>(
  pathname: Pathname,
  ...[pathParams]: GetPathParams<Pathname>
): string => {
  if (!pathParams) {
    return originalGeneratePath(pathname);
  }

  let path: string;

  if ('params' in pathParams) {
    const params = stringifyParamsObject(pathParams.params);
    // @FIXME react router params type is incompatible with StingifiedParams
    // react-router is not exporting PathParams type, so we cannot re-use it
    path = originalGeneratePath(pathname, params as any);
  } else {
    path = originalGeneratePath(pathname);
  }

  if ('queryParams' in pathParams) {
    const queryParams = stringifyParamsObject(pathParams.queryParams);
    const searchQuery = createSearchParams(queryParams);
    path = `${path}?${searchQuery.toString()}`;
  }

  return path;
};

type Params = Record<string, string | string[] | number | number[] | undefined | null>;
type ParamsEntries<T> = [keyof T, string | number | string[] | number[]][];
type StringifiedParams<T> = { [Key in keyof T]: string };

export const stringifyParamsObject = <T extends Params>(params: T): StringifiedParams<T> => {
  const entries = Object.entries(params) as ParamsEntries<T>;

  return entries.reduce((acc, [key, value]) => {
    if (value) {
      acc[key] = Array.isArray(value) ? value.join(',') : value.toString();
    }

    return acc;
  }, {} as StringifiedParams<T>);
};
